import { useState, useEffect, useCallback, useRef } from 'react';
import { saveDeviceToken } from '../api/device/saveDeviceToken';
import { getDeviceInfo, getCurrentLocation, getLocationInfo } from '../utils/deviceInfo';

export function useDeviceToken() {
    const [isRegistered, setIsRegistered] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isRegistering, setIsRegistering] = useState(false);
    const registerDeviceRef = useRef(null);

    const updateFirebaseToken = useCallback(async (firebaseToken) => {
        // Backend requires non-empty firebase_token (400 EMPTY_FIREBASE_TOKEN).
        // Skip the POST until we actually have an FCM token — device will be
        // registered later when Firebase finally resolves it.
        const token = (firebaseToken || '').trim();
        if (!token) {
            return;
        }

        if (!isRegistered) {
            if (registerDeviceRef.current) {
                await registerDeviceRef.current(token);
            }
            return;
        }

        try {
            const deviceInfo = await getDeviceInfo();
            deviceInfo.firebase_token = token;
            // Force update even if device is already registered (for token updates)
            await saveDeviceToken(deviceInfo, true);
        } catch (err) {
            setError(err?.message || err?.toString() || '');
        }
    }, [isRegistered]);

    const registerDevice = useCallback(async (firebaseToken = null) => {
        // Never send a request without a real Firebase token
        if (!firebaseToken) {
            return;
        }

        // If device is already registered and token is provided, update token instead
        if (isRegistered) {
            await updateFirebaseToken(firebaseToken);
            return;
        }

        // Prevent multiple simultaneous registrations
        if (isRegistering) {
            return;
        }

        setIsRegistering(true);
        setIsLoading(true);
        setError(null);

        try {
            const deviceInfo = await getDeviceInfo();

            // Override firebase token if explicitly provided, otherwise use the one from getDeviceInfo()
            if (firebaseToken) {
                deviceInfo.firebase_token = firebaseToken;
            }

            // Backend requires non-empty firebase_token (400 EMPTY_FIREBASE_TOKEN).
            // If we still don't have one (Firebase unavailable / VAPID not set /
            // user declined notifications), skip the register-device POST entirely
            // and let the caller retry once the FCM token resolves.
            const token = (deviceInfo.firebase_token || '').trim();
            if (!token) {
                setIsRegistering(false);
                setIsLoading(false);
                return;
            }
            deviceInfo.firebase_token = token;

            // Try to get location
            try {
                const location = await getCurrentLocation();
                if (location) {
                    const locationInfo = await getLocationInfo(location.latitude, location.longitude);
                    deviceInfo.location = {
                        latitude: location.latitude,
                        longitude: location.longitude,
                        country: locationInfo.country,
                        locality: locationInfo.locality
                    };
                }
            } catch (locationError) {
                // Continue without location
            }

            // Save to backend
            await saveDeviceToken(deviceInfo);

            // Mark as registered
            localStorage.setItem('device_registered', 'true');
            setIsRegistered(true);
        } catch (err) {
            const errorMessage = err?.message || err?.toString() || '';
            setError(errorMessage);

            // Don't mark as registered if there was an error
            setIsRegistered(false);
        } finally {
            setIsLoading(false);
            setIsRegistering(false);
        }
    }, [isRegistering, isRegistered, updateFirebaseToken]);

    // Update ref when registerDevice changes
    useEffect(() => {
        registerDeviceRef.current = registerDevice;
    }, [registerDevice]);

    // Check if device is already registered on mount
    useEffect(() => {
        const isDeviceRegistered = localStorage.getItem('device_registered') === 'true';
        setIsRegistered(isDeviceRegistered);
    }, []);

    const resetDeviceRegistration = () => {
        localStorage.removeItem('device_registered');
        localStorage.removeItem('device_id');
        setIsRegistered(false);
        setIsRegistering(false);
        setError(null);
    };

    return {
        isRegistered,
        isLoading,
        error,
        registerDevice,
        updateFirebaseToken,
        resetDeviceRegistration
    };
}
